/* eslint-disable max-statements */
import { chakra, forwardRef, useMultiStyleConfig } from '@chakra-ui/system';
import { callAllHandlers, getBox } from '@chakra-ui/utils';
import { StarBold } from '@lego/klik-ui-icons';
import { Tooltip } from '@lego/klik-ui-tooltip';
import * as React from 'react';

export interface IRatingProps {
  /**
   * The rating (e.g 50%) to display.
   * Provide the value as percentage (0..100)
   */
  value: number;
  /**
   * The number of icons (e.g. "stars") to display.
   * @default 5
   */
  max?: number;
  /**
   * The icon to use.
   * @default StarBold from klik-ui-icons
   */
  icon?: React.ReactNode;
  /**
   * Color of the 'active' stars
   * @default Gold (#F2A705)
   */
  color?: string;
  /**
   * Should we just _display_ the rating (`readonly = true`) or also allow _setting_ a rating?
   * @default false
   */
  readonly?: boolean;
  /**
   * Disable the component.
   * @default false
   */
  disabled?: boolean;
  /**
   * Handler to call when a rating has been comitted.
   * The $value argument will be a percentage (0..100)
   */
  onChange?: (value: number) => void;
  /**
   * Display the rating as a tooltip?
   * @default false
   */
  tooltip?: boolean;
  /**
   * Allow half-steps?
   * @default false
   * If FALSE we round to nearest whole step
   */
  fractional?: boolean;
  /**
   * Size.
   */
  size?: 'lg' | 'md' | 'sm';
}

export const Rating = forwardRef<IRatingProps, 'div'>(
  (
    {
      value = 50,
      max = 5,
      icon = <StarBold />,
      color = '#F2A705',
      readonly = false,
      disabled = false,
      tooltip = false,
      fractional = false,
      onKeyDown: onKeyDownProp,
      onChange: onChangeProp = (_value: number) => void {},
      ...rest
    },
    ref
  ) => {
    const styles = useMultiStyleConfig('Rating', rest);
    const containerStyle = styles.container;
    const maskStyle = styles.mask;
    const trackStyle = styles.track;
    const nums = new Array(max).fill(0).map((_v, i) => i);
    const trackRef = React.createRef<HTMLElement>();
    const fullstep = 100.0 / max;
    const halfstep = fullstep / 2;

    if (disabled) {
      tooltip = false;
      readonly = true;
      maskStyle.color = 'rgba(0,0,0,0.2)'; // black 20%
    } else {
      maskStyle.color = color;
    }

    if (!readonly) {
      trackStyle.cursor = 'pointer';
    }

    const [ratingPercent, setRatingPercent] = React.useState(0);
    const [initialRatingPercent, setInitialRatingPercent] = React.useState(0);
    const [isInteractive, setIsInteractive] = React.useState(false);
    const [trackRect, setTrackRect] = React.useState({ left: 0, width: 0 });

    // used to filter out mouseMove events with same value as previous event
    let prevX = -1;

    // Utils --

    const getRating = (): number => {
      return (ratingPercent / 100.0) * max;
    };

    const formatRating = (): string => {
      const v = (ratingPercent / 100.0) * max;

      if (!fractional) {
        return v.toFixed(0);
      }

      let str = v.toFixed(1);

      str = str.replace('0.5', '.5');
      str = str.replace('.0', '');
      str = str.replace('.5', '½');
      // str += ` / ${max}`;

      return str;
    };

    const roundFractional = (pct: number): number => {
      const v = fractional ? halfstep : fullstep;

      return Math.round(pct / v) * v;
    };

    const maybeSetInteractive = () => {
      if (!readonly || !disabled) {
        setIsInteractive(true);
      }
    };

    const setRatingPercentRounded = (pct: number): void => {
      setRatingPercent(roundFractional(pct));
    };

    // converts "2px 2px 3px 2px" -> [2,2,3,2]
    const getLRPadding = (paddingString = '0px 0px 0px 0px'): number[] => {
      const parts = paddingString
        .replace(/px/gu, '')
        .split(/ /gu)
        .map((e) => parseInt(e.trim()));

      return [parts[1], parts[3]];
    };

    const getTooltipPosition = (): string => {
      const padding = containerStyle.padding
        ? getLRPadding(containerStyle.padding as string)
        : [2, 2]; // default padding

      const index = Math.max(0, Math.ceil(ratingPercent / fullstep) - 1);
      const tw = trackRect.width - (padding[0] + padding[1]);
      const sw = tw / max; // computed width of each "star" box
      const pos = padding[0] + (tw - sw * index) - sw / 2;

      return `${pos}px`;
    };

    // Set rating
    const setRatingFromHover = React.useCallback(
      (x: number) => {
        const length = trackRect.width;
        const offset = fractional ? 0 : 8; // ?
        const diff = x + offset - trackRect.left;
        const p = Math.round(Math.max(0, Math.min(100, (diff / length) * 100)));
        setRatingPercentRounded(p);
      },
      [fractional, trackRect]
    );

    const setRatingFromClick = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (readonly || disabled) {
          return;
        }

        setRatingFromHover(event.clientX);
        setInitialRatingPercent(ratingPercent);
        onChangeProp(ratingPercent);
      },
      [readonly, disabled, ratingPercent, trackRef]
    );

    const setRatingFromKeyboard = React.useCallback(
      (up = true) => {
        const nearest = Math.round(ratingPercent);
        const wholeStarAmount = 100.0 / max; // whole-star steps
        const halfStarAmount = 50.0 / max; // half-star steps
        const amount = fractional ? halfStarAmount : wholeStarAmount;
        const v = up ? nearest + amount : nearest - amount;
        const clampedValue = Math.max(0, Math.min(100, v));

        setRatingPercent(initialRatingPercent);

        setRatingPercentRounded(clampedValue);
        onChangeProp(clampedValue);
      },
      [trackRef, fractional, max, ratingPercent]
    );

    // MOUSE --
    const handleMouseEnter = React.useCallback(
      (_e: React.MouseEvent<HTMLDivElement>) => {
        maybeSetInteractive();
      },
      [disabled, readonly]
    );

    const handleMouseMove = React.useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (readonly || disabled) {
          return;
        }

        const x = e.clientX;

        if (prevX !== x) {
          setRatingFromHover(x);
        }

        prevX = x;
      },
      [disabled, readonly, prevX, trackRect]
    );

    const handleMouseLeave = React.useCallback(
      (_e: React.MouseEvent<HTMLDivElement>) => {
        if (readonly || disabled) {
          return;
        }

        setIsInteractive(false);
        // re-render with initial value (as we require the user to click/tap to actually commit the value)
        setRatingPercent(initialRatingPercent);
      },
      [disabled, readonly, initialRatingPercent, ratingPercent]
    );

    // KBD --
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled || readonly) {
          return;
        }

        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowUp':
            setIsInteractive(true);
            e.preventDefault();
            setRatingFromKeyboard(true);
            break;
          case 'ArrowLeft':
          case 'ArrowDown':
            setIsInteractive(true);
            e.preventDefault();
            setRatingFromKeyboard(false);
            break;
          case 'Escape':
          case 'Tab':
            setIsInteractive(false);
            break;
        }
      },
      [disabled, readonly, ratingPercent]
    );

    // TOUCH --
    const handleTouchMove = React.useCallback(
      (e: React.TouchEvent<HTMLDivElement>) => {
        if (readonly || disabled) {
          return;
        }

        const x = e.touches[0].clientX;

        if (prevX !== x) {
          setRatingFromHover(x);
        }

        prevX = x;
      },
      [disabled, readonly, prevX, trackRect]
    );

    // ARIA --
    const handleFocus = React.useCallback(
      (_e: React.FocusEvent<HTMLDivElement>) => {
        maybeSetInteractive();
      },
      [disabled, readonly]
    );

    const handleBlur = React.useCallback(
      (_e: React.FocusEvent<HTMLDivElement>) => {
        setIsInteractive(false);
      },
      [disabled, readonly]
    );

    // Init --
    React.useEffect(() => {
      const clampedInputValue = Math.max(0, Math.min(100, value));
      const percent = fractional ? Math.round(clampedInputValue) : clampedInputValue;

      setRatingPercentRounded(percent);
      setInitialRatingPercent(roundFractional(percent));

      if (trackRef.current && trackRect.width === 0) {
        const { left, width } = getBox(trackRef.current).borderBox;
        setTrackRect({ left, width });
      }
    }, [value, fractional]);

    React.useEffect(() => {
      if (trackRef.current && trackRect.width === 0) {
        const { left, width } = getBox(trackRef.current).borderBox;
        setTrackRect({ left, width });
      }
    }, [trackRef]);

    return (
      <chakra.div
        __css={containerStyle}
        aria-disabled={disabled ? 'true' : 'false'}
        aria-label={`Rating: ${formatRating()} out of ${max} stars`}
        aria-readonly={readonly ? 'true' : 'false'}
        aria-valuemax={max}
        aria-valuemin={0}
        aria-valuenow={getRating()}
        aria-valuetext={readonly ? formatRating() : 'Disabled'}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={callAllHandlers(onKeyDownProp, handleKeyDown)}
        ref={ref as React.RefObject<HTMLDivElement>}
        role="slider"
        tabIndex={readonly || disabled ? -1 : 0}
        {...rest}
      >
        {nums.map((v) => (
          <chakra.div __css={styles.icons} aria-hidden="true" key={`star${v}`}>
            {icon}
          </chakra.div>
        ))}

        <chakra.div
          __css={maskStyle}
          aria-hidden="true"
          style={{ right: `${100 - ratingPercent}%` }}
        >
          {nums.map((v) => (
            <chakra.div __css={styles.icons} aria-hidden="true" key={`rate${v}`}>
              {icon}
            </chakra.div>
          ))}
        </chakra.div>

        {tooltip && (
          <Tooltip
            aria-hidden="true"
            closeOnClick={false}
            isDisabled={disabled}
            isOpen={isInteractive}
            label={`${formatRating()}`}
            minW="4rem"
            placement="top"
            size="sm"
            textAlign="center"
          >
            <chakra.div
              __css={styles.tooltip}
              aria-hidden="true"
              style={{ right: getTooltipPosition() }}
            />
          </Tooltip>
        )}

        <chakra.div
          __css={trackStyle}
          id="trackRef"
          onClick={setRatingFromClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
          onTouchMove={handleTouchMove}
          ref={trackRef as React.RefObject<HTMLDivElement>}
        />
      </chakra.div>
    );
  }
);
